import { Link } from "react-router-dom";
import { Instagram, Youtube, Mail, Linkedin } from "lucide-react";

export default function Footer() {
  return (
    <footer className="bg-[#1A1A1A] text-white relative overflow-hidden" data-testid="site-footer">
      {/* Marquee tagline — 6 words, alternating Gold/White/Red, refined size */}
      <div className="border-b border-white/10 overflow-hidden py-6">
        <div className="ticker-track" data-testid="footer-marquee">
          {Array.from({ length: 4 }).map((_, i) => {
            const WORDS = ["Roar", "Rise", "Radiate", "Belong", "Connect", "Serve"];
            const COLORS = ["#F4C430", "#FFFFFF", "#8B1E2D"];
            return (
              <div key={i} className="flex items-center pr-4">
                {WORDS.map((w, j) => (
                  <span key={j} className="flex items-center">
                    <span
                      className="font-playfair italic font-medium text-2xl md:text-3xl px-4 whitespace-nowrap"
                      style={{ color: COLORS[j % 3] }}
                    >
                      {w}
                    </span>
                    <span className="w-1.5 h-1.5 rounded-full bg-[#F4C430] inline-block mx-1" aria-hidden="true" />
                  </span>
                ))}
              </div>
            );
          })}
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 py-20 grid grid-cols-1 md:grid-cols-12 gap-12">
        <div className="md:col-span-5">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-11 h-11 overflow-hidden rounded-full bg-[#8B1E2D] flex-shrink-0 ring-1 ring-white/10">
              <img
                src="https://customer-assets.emergentagent.com/job_leo-carter-impact/artifacts/7nvj4c0n_LEO%20CLUB%20OF%20MUMBAI%20CARTER%20ROAD%20%281%29.png"
                alt="Leo Club of Mumbai Carter Road"
                className="w-full h-full object-cover"
                style={{ transform: "scale(1.32)" }}
              />
            </div>
            <div className="leading-tight">
              <div className="font-playfair font-semibold text-lg">Leo Club</div>
              <div className="font-inter text-[10px] tracking-[0.2em] uppercase text-white/50">
                Mumbai · Carter Road
              </div>
            </div>
          </div>
          <p className="font-playfair italic text-xl md:text-2xl text-white/80 max-w-md leading-snug">
            The smallest act of service ripples further than you know.
          </p>
          <div className="mt-8 flex items-center gap-3">
            <a
              href="https://www.youtube.com/@leoclubofmcr/videos"
              target="_blank"
              rel="noreferrer"
              className="w-11 h-11 flex items-center justify-center border border-white/15 hover:bg-[#8B1E2D] hover:border-[#8B1E2D] transition-colors rounded-full"
              data-testid="footer-youtube"
              aria-label="YouTube"
            >
              <Youtube size={17} />
            </a>
            <a
              href="https://instagram.com/leoclubofmcr"
              target="_blank"
              rel="noreferrer"
              className="w-11 h-11 flex items-center justify-center border border-white/15 hover:bg-[#8B1E2D] hover:border-[#8B1E2D] transition-colors rounded-full"
              data-testid="footer-instagram"
              aria-label="Instagram"
            >
              <Instagram size={17} />
            </a>
            <a
              href="https://www.linkedin.com/company/leo-club-of-mumbai-carter-road/about/"
              target="_blank"
              rel="noreferrer"
              className="w-11 h-11 flex items-center justify-center border border-white/15 hover:bg-[#8B1E2D] hover:border-[#8B1E2D] transition-colors rounded-full"
              data-testid="footer-linkedin"
              aria-label="LinkedIn"
            >
              <Linkedin size={17} />
            </a>
            <a
              href="https://mail.google.com/mail/?view=cm&fs=1&to=leoclubofmumbaicarterroad@gmail.com"
              target="_blank"
              rel="noreferrer"
              title="leoclubofmumbaicarterroad@gmail.com"
              className="w-11 h-11 flex items-center justify-center border border-white/15 hover:bg-[#8B1E2D] hover:border-[#8B1E2D] transition-colors rounded-full"
              data-testid="footer-email"
              aria-label="Email leoclubofmumbaicarterroad@gmail.com"
            >
              <Mail size={17} />
            </a>
          </div>
        </div>

        <div className="md:col-span-3">
          <div className="font-inter text-[11px] tracking-[0.25em] uppercase text-[#F4C430] mb-5">
            Explore
          </div>
          <ul className="space-y-3 font-inter text-sm text-white/80">
            <li><Link to="/about" className="hover:text-white transition-colors" data-testid="footer-link-about">About</Link></li>
            <li><Link to="/events" className="hover:text-white transition-colors" data-testid="footer-link-events">Events</Link></li>
            <li><Link to="/leadership" className="hover:text-white transition-colors" data-testid="footer-link-leadership">Leadership</Link></li>
            <li><Link to="/join" className="hover:text-white transition-colors" data-testid="footer-link-join">Join Us</Link></li>
            <li><Link to="/contact" className="hover:text-white transition-colors" data-testid="footer-link-contact">Contact</Link></li>
          </ul>
        </div>

        <div className="md:col-span-4">
          <div className="font-inter text-[11px] tracking-[0.25em] uppercase text-[#F4C430] mb-5">
            Roar Room
          </div>
          <p className="font-inter text-sm text-white/70 leading-relaxed mb-4">
            Conversations on leadership, growth, and real stories that shape how we think and lead.
          </p>
          <a
            href="https://www.youtube.com/@leoclubofmcr/videos"
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 font-inter text-sm text-white border-b border-[#F4C430] pb-1 hover:text-[#F4C430] transition-colors"
            data-testid="footer-watch-episodes"
          >
            Watch Episodes →
          </a>

          <div className="mt-8">
            <div className="font-inter text-[11px] tracking-[0.25em] uppercase text-[#F4C430] mb-3">
              Stay Connected
            </div>
            <p className="font-inter text-xs text-white/60">
              Reach out at <a href="mailto:leoclubofmumbaicarterroad@gmail.com" className="underline decoration-[#F4C430] underline-offset-4">leoclubofmumbaicarterroad@gmail.com</a>
            </p>
          </div>
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 py-6 flex flex-col md:flex-row items-center justify-between gap-3">
          <p className="font-inter text-[12px] text-white/50">
            Built with purpose by Leo Club of Mumbai Carter Road · © 2026 · Designed by{" "}
            <a
              href="https://www.sakinaabbas.com"
              target="_blank"
              rel="noreferrer"
              className="text-white/50 underline decoration-[#F4C430] underline-offset-4 transition-all"
              data-testid="footer-designer-credit"
            >
              Sakina Abbas
            </a>
          </p>
          <p className="font-inter text-[11px] tracking-[0.25em] uppercase text-white/40">
            Part of Lions Clubs International
          </p>
        </div>
      </div>
    </footer>
  );
}
